import React, { MouseEvent, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Icon, useToast } from '@pairy/pds-react';

import useUpdateClientLike from '@/hooks/mutations/client/useUpdateClientLike';
import { CLIENT_LIST_QUERY_KEY } from '@/hooks/queries/client/useClientList';
import Tooltip from '@/components/User/_components/ui/Tooltip';

import * as Styled from './index.styles';

interface Props {
  like: boolean;
  clientId: number;
  width?: number;
  height?: number;
}

const Star = ({ like, clientId, width = 14, height = 14 }: Props) => {
  const [isLike, setIsLike] = useState(like);
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate } = useUpdateClientLike();

  const handleClickLike = (e: MouseEvent<HTMLButtonElement>) => {
    // 카드 전체가 클릭 대상인 목록에서도 별 토글이 이동으로 새지 않게 한다.
    e.stopPropagation();

    const previous = isLike;
    setIsLike(!previous);

    mutate(
      { clientId },
      {
        // 실패하면 낙관적으로 뒤집은 표시를 되돌린다. 되돌리지 않으면
        // 화면은 켜져 있는데 서버는 꺼져 있는 상태가 그대로 남는다.
        onError: () => {
          setIsLike(previous);
          toast('즐겨찾기 변경에 실패했어요. 잠시 후 다시 시도해 주세요.');
        },
        // 좋아요는 고객 목록의 1차 정렬 기준이라 행 순서까지 바뀐다.
        onSettled: () => {
          queryClient.invalidateQueries(CLIENT_LIST_QUERY_KEY);
        },
      },
    );
  };

  // 목록을 다시 받아오면(정렬·페이지 이동 포함) 서버 값으로 맞춘다.
  React.useEffect(() => {
    setIsLike(like);
  }, [like, setIsLike]);

  return (
    <Tooltip content={isLike ? '상단 고정 해제' : <>상단 고정</>}>
      {/*
        aria-label은 상태가 아닌 동작 대상으로 고정하고 켜짐 여부는 aria-pressed로 알린다.
        (토글 버튼의 표준 패턴 — 라벨까지 바뀌면 상태가 두 번 읽힌다)
      */}
      <Styled.StarButton
        type="button"
        onClick={handleClickLike}
        aria-pressed={isLike}
        aria-label="즐겨찾기"
      >
        <Icon
          icon={isLike ? 'ph:star-fill' : 'ph:star-light'}
          color={isLike ? 'star' : 'zinc_300'}
          size={{
            width,
            height,
          }}
        />
      </Styled.StarButton>
    </Tooltip>
  );
};

export default Star;
